/* Proposal AI chat — the conversational editor that lives beside the
   proposal document (ProposalDocEditor). The user asks for changes in plain
   English; the panel auto-saves any pending manual edits, sends the
   instruction to POST /:id/prompt, adopts the server-authoritative spec back
   into the editor and shows a deterministic, client-computed DIFF of what
   moved (case adds/removes/renames, bullets added/removed, overview and the
   other narrative sections, timeline phases, and the setup/monthly fee
   blocks). Prices are NEVER part of the proposal spec, so nothing here can
   leak them.

   The panel owns only presentation + history; the correctness core (save
   race guard, adoption, changed-section flash + scroll) lives in the editor
   and is handed here through the single async `onSend` prop. Messages persist
   per-proposal in sessionStorage (in-flight state never does).

   Desktop: a 360px right side panel that shrinks the page canvas. Mobile
   (≤900px): a slide-up bottom sheet with a tap-to-close backdrop. English
   strings, scoped .prop-chat-* styles in proposal-editor.css (this surface
   must NOT touch architect.css — that file is P3's). */

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { ProposalSpec, ProposalTelemetry } from './proposalApi';
import { PROPOSAL_MODELS } from './ProposalPanel';
import { autoGrow, fmtK } from './utils';
import { ArrowUp, MessageCircle, Pencil, Trash2, X } from 'lucide-react';
import { LatoAiLauncher, LatoOrb, useLatoAiPanelDrag } from './LatoAi';

/* ── small text helpers ─────────────────────────────────────── */

const norm = (s: string | undefined | null): string => (s || '').trim();
const sameName = (a: string | undefined, b: string | undefined): boolean =>
  norm(a).toLowerCase() === norm(b).toLowerCase();

/** Join a string array into a stable comparison signature (trim, drop empty). */
function arrSig(a: string[] | undefined): string {
  return (a || [])
    .map((s) => norm(s))
    .filter(Boolean)
    .join('');
}

/** Signature of a { name, text, category? } list — order-sensitive, so a
    reorder or an edit both register as a change. */
function namedSig(a: { name?: string; text?: string; category?: string }[] | undefined): string {
  return (a || [])
    .map((x) => norm(x.name) + '' + norm(x.text) + '' + norm(x.category))
    .join('');
}

/* ── deterministic diff ─────────────────────────────────────── */

export type ChipKind =
  | 'add'
  | 'remove'
  | 'rename'
  | 'edit'
  | 'note'
  | 'section'
  | 'more';

export interface DiffChip {
  id: string;
  kind: ChipKind;
  label: string;
}

/** A stable key for a case section (its trimmed name), used both to tag diff
    results and to flash the matching section after adoption. */
export function caseKey(name: string | undefined): string {
  return norm(name);
}

interface Matcher<T> {
  used: Set<number>;
  pool: T[];
}
function makeMatcher<T>(pool: T[]): Matcher<T> {
  return { used: new Set(), pool };
}
/** Match by name first (case-insensitive, unused), else fall back to the same
    index when still free — mirrors the estimate chat's matching rule. */
function matchByName<T extends { name?: string }>(
  m: Matcher<T>,
  name: string | undefined,
  idx: number,
): { item: T; index: number } | null {
  let j = m.pool.findIndex((p, i) => !m.used.has(i) && sameName(p.name, name));
  if (j < 0 && idx < m.pool.length && !m.used.has(idx)) j = idx;
  if (j < 0) return null;
  m.used.add(j);
  return { item: m.pool[j], index: j };
}

/** True when a case's narrative or bullets moved (used both for chips and the
    changed-section flash). */
function caseChanged(
  b: { name?: string; opening?: string; bullets?: string[]; closing?: string },
  a: { name?: string; opening?: string; bullets?: string[]; closing?: string },
): boolean {
  return (
    !sameName(b.name, a.name) ||
    norm(b.opening) !== norm(a.opening) ||
    norm(b.closing) !== norm(a.closing) ||
    arrSig(b.bullets) !== arrSig(a.bullets)
  );
}

/** The set of case keys that were ADDED or CHANGED between two specs — the
    sections the editor flashes after adopting the new spec. Removed cases are
    gone, so they never flash. */
export function changedCaseKeys(before: ProposalSpec, after: ProposalSpec): Set<string> {
  const keys = new Set<string>();
  const matcher = makeMatcher(before.cases || []);
  const aCases = after.cases || [];
  for (let ci = 0; ci < aCases.length; ci++) {
    const ac = aCases[ci];
    const bm = matchByName(matcher, ac.name, ci);
    if (!bm || caseChanged(bm.item, ac)) keys.add(caseKey(ac.name));
  }
  return keys;
}

/** First changed case key in document order (for scroll-into-view). */
export function firstChangedCaseKey(after: ProposalSpec, keys: Set<string>): string | null {
  for (const c of after.cases || []) {
    const k = caseKey(c.name);
    if (keys.has(k)) return k;
  }
  return null;
}

let chipSeq = 0;
const chip = (kind: ChipKind, label: string): DiffChip => ({
  id: 'pchip' + ++chipSeq,
  kind,
  label,
});

/** The full ordered chip list for the assistant bubble. Capped at 8 visible
    chips (+N more). */
export function computeDiff(before: ProposalSpec, after: ProposalSpec): DiffChip[] {
  const chips: DiffChip[] = [];

  // ── cases ──
  const matcher = makeMatcher(before.cases || []);
  const aCases = after.cases || [];
  for (let ci = 0; ci < aCases.length; ci++) {
    const ac = aCases[ci];
    const bm = matchByName(matcher, ac.name, ci);
    if (!bm) {
      chips.push(chip('add', '+ Case "' + norm(ac.name) + '"'));
      continue;
    }
    const bc = bm.item;
    if (!sameName(bc.name, ac.name)) {
      chips.push(chip('rename', 'Case renamed: ' + norm(bc.name) + ' → ' + norm(ac.name)));
    }
    const bCount = (bc.bullets || []).map(norm).filter(Boolean).length;
    const aCount = (ac.bullets || []).map(norm).filter(Boolean).length;
    if (aCount > bCount) {
      chips.push(chip('add', norm(ac.name) + ': +' + (aCount - bCount) + ' bullet' + (aCount - bCount === 1 ? '' : 's')));
    } else if (aCount < bCount) {
      chips.push(chip('remove', norm(ac.name) + ': −' + (bCount - aCount) + ' bullet' + (bCount - aCount === 1 ? '' : 's')));
    } else if (
      arrSig(bc.bullets) !== arrSig(ac.bullets) ||
      norm(bc.opening) !== norm(ac.opening) ||
      norm(bc.closing) !== norm(ac.closing)
    ) {
      chips.push(chip('edit', norm(ac.name) + ' updated'));
    }
  }
  (before.cases || []).forEach((bc, i) => {
    if (!matcher.used.has(i)) chips.push(chip('remove', '− Case "' + norm(bc.name) + '"'));
  });

  // ── narrative sections ──
  if (norm(before.overview) !== norm(after.overview)) chips.push(chip('section', 'Overview updated'));
  if (norm(before.workflow_intro) !== norm(after.workflow_intro)) chips.push(chip('section', 'Workflow intro updated'));
  if (norm(before.please_note) !== norm(after.please_note)) chips.push(chip('note', 'Please-note updated'));

  // ── API integrations ──
  chips.push(...countChip('API', (before.api_integrations || []).length, (after.api_integrations || []).length,
    namedSig(before.api_integrations) !== namedSig(after.api_integrations)));

  // ── confirmations ──
  chips.push(...countChip('Confirmation', (before.confirmations || []).length, (after.confirmations || []).length,
    namedSig(before.confirmations) !== namedSig(after.confirmations)));
  if (norm(before.confirmations_intro) !== norm(after.confirmations_intro)) {
    chips.push(chip('section', 'Confirmations intro updated'));
  }

  // ── dashboard ──
  const bD = before.dashboard || null;
  const aD = after.dashboard || null;
  if (!bD && aD) chips.push(chip('add', '+ Dashboard section'));
  else if (bD && !aD) chips.push(chip('remove', '− Dashboard section'));
  else if (bD && aD && (
    norm(bD.intro) !== norm(aD.intro) ||
    norm(bD.extra) !== norm(aD.extra) ||
    namedSig(bD.capabilities) !== namedSig(aD.capabilities)
  )) {
    chips.push(chip('edit', 'Dashboard updated'));
  }

  // ── efficiency ──
  const bE = before.efficiency || {};
  const aE = after.efficiency || {};
  if (
    norm(bE.opening) !== norm(aE.opening) ||
    norm(bE.closing) !== norm(aE.closing) ||
    arrSig(bE.development) !== arrSig(aE.development) ||
    arrSig(bE.benefits) !== arrSig(aE.benefits)
  ) {
    chips.push(chip('section', 'Efficiency updated'));
  }

  // ── timeline ──
  const bT = before.timeline || [];
  const aT = after.timeline || [];
  const tSig = (t: { phase?: string; details?: string }[]) =>
    t.map((x) => norm(x.phase) + '' + norm(x.details)).join('');
  if (tSig(bT) !== tSig(aT)) chips.push(chip('section', 'Timeline updated'));

  // ── fee blocks ──
  chips.push(...countChip('Setup fee', (before.setup_fees || []).length, (after.setup_fees || []).length,
    feeSetupSig(before) !== feeSetupSig(after)));
  chips.push(...countChip('Monthly fee', (before.monthly_fees || []).length, (after.monthly_fees || []).length,
    feeMonthlySig(before) !== feeMonthlySig(after)));

  // ── monthly notes ──
  const bn = (before.monthly_notes || []).map(norm).filter(Boolean);
  const an = (after.monthly_notes || []).map(norm).filter(Boolean);
  if (an.length > bn.length) chips.push(chip('note', 'Monthly note added'));
  else if (an.length < bn.length) chips.push(chip('note', 'Monthly note removed'));
  else if (arrSig(before.monthly_notes) !== arrSig(after.monthly_notes)) chips.push(chip('note', 'Monthly notes updated'));

  // cap at 8 visible (+N more)
  if (chips.length > 8) {
    const overflow = chips.length - 7;
    return chips.slice(0, 7).concat(chip('more', '+' + overflow + ' more'));
  }
  return chips;
}

/** A count-delta chip ("+2 API" / "−1 Confirmation") or, when the count is
    unchanged but the content moved, a single "updated" chip. */
function countChip(label: string, before: number, after: number, contentChanged: boolean): DiffChip[] {
  if (after > before) return [chip('add', '+' + (after - before) + ' ' + label + (after - before === 1 ? '' : 's'))];
  if (after < before) return [chip('remove', '−' + (before - after) + ' ' + label + (before - after === 1 ? '' : 's'))];
  if (contentChanged) return [chip('edit', label + 's updated')];
  return [];
}

function feeSetupSig(s: ProposalSpec): string {
  return (s.setup_fees || [])
    .map((f) => norm(f.case) + '' + norm(f.description) + '' + arrSig(f.apis) + '' + arrSig(f.notes))
    .join('');
}
function feeMonthlySig(s: ProposalSpec): string {
  return (s.monthly_fees || [])
    .map((f) => norm(f.label) + '' + arrSig(f.bullets))
    .join('');
}

/* ── telemetry footnote ─────────────────────────────────────── */

function telemetryText(t: ProposalTelemetry): string {
  const inT = t.inputTokens ?? 0;
  const outT = t.outputTokens ?? 0;
  const cache = (t.cacheReadTokens ?? 0) + (t.cacheCreationTokens ?? 0);
  const parts = ['↑' + fmtK(inT) + ' ↓' + fmtK(outT) + ' tokens'];
  if (cache > 0) parts.push('cache ' + fmtK(cache));
  if (typeof t.costUsd === 'number' && t.costUsd > 0) {
    parts.push('$' + t.costUsd.toFixed(t.costUsd < 0.1 ? 3 : 2));
  }
  return parts.join(' · ');
}

/* ── message model + persistence ────────────────────────────── */

type ChatMessage =
  | { id: string; role: 'user'; text: string }
  | { id: string; role: 'answer'; text: string }
  | { id: string; role: 'system'; text: string }
  | {
      id: string;
      role: 'assistant';
      status: string;
      chips: DiffChip[];
      telemetry: string | null;
    }
  | { id: string; role: 'error'; text: string; instruction: string; model: string; mode?: 'ask' | 'edit' };

const HISTORY_CAP = 40;
const historyKey = (id: string): string => 'arch-prop-chat:' + id;

function loadHistory(id: string): ChatMessage[] {
  try {
    const raw = sessionStorage.getItem(historyKey(id));
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? (arr as ChatMessage[]) : [];
  } catch {
    return [];
  }
}

function saveHistory(id: string, msgs: ChatMessage[]): void {
  try {
    sessionStorage.setItem(historyKey(id), JSON.stringify(msgs.slice(-HISTORY_CAP)));
  } catch {
    /* quota / disabled storage — non-fatal */
  }
}

let msgSeq = 0;
const nextId = (): string => 'pmsg' + Date.now().toString(36) + (++msgSeq);

const QUICK_PROMPTS = [
  'Tighten the overview and make it punchier',
  'Add a confirmation about API access',
  'Make Case 1 workflow more detailed',
];

/* ── props ──────────────────────────────────────────────────── */

export interface ChatEditResult {
  ok: boolean;
  before?: ProposalSpec;
  after?: ProposalSpec;
  telemetry?: ProposalTelemetry | null;
  error?: string;
}

interface Props {
  proposalId: string;
  open: boolean;
  onClose: () => void;
  onOpen?: () => void;
  openDisabled?: boolean;
  /** True while the editor's manual Save is in flight — send is disabled. */
  saving: boolean;
  readOnly?: boolean;
  onAsk?: (question: string, model: string) => Promise<{ answer: string; telemetry?: ProposalTelemetry | null }>;
  onDraftStateChange?: (state: { dirty: boolean; busy: boolean }) => void;
  /** The correctness core (auto-save → snapshot → POST → adopt → flash),
      owned by the editor. Resolves with before/after specs + telemetry on
      success, or { ok:false, error } otherwise. Calls hooks.onSavedManual the
      moment pending manual edits were persisted. */
  onSend: (
    instruction: string,
    model: string,
    hooks: { onSavedManual: () => void },
  ) => Promise<ChatEditResult>;
}

/* ── component ──────────────────────────────────────────────── */

export default function ProposalChat({ proposalId, open, onClose, onOpen, openDisabled, saving, onSend, onAsk, readOnly = false, onDraftStateChange }: Props) {
  const [messages, setMessages] = useState<ChatMessage[]>(() => loadHistory(proposalId));
  const [input, setInput] = useState('');
  const [model, setModel] = useState(PROPOSAL_MODELS[0].value);
  const [mode, setMode] = useState<'ask'|'edit'>(onAsk ? 'ask' : 'edit');
  useEffect(() => { if (readOnly && onAsk) setMode('ask'); }, [readOnly, onAsk]);
  const [inFlight, setInFlight] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  useEffect(() => { onDraftStateChange?.({ dirty: !!input.trim(), busy: inFlight }); }, [input, inFlight, onDraftStateChange]);
  useEffect(() => () => onDraftStateChange?.({ dirty: false, busy: false }), [onDraftStateChange]);

  const listRef = useRef<HTMLDivElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const stickRef = useRef(true); // auto-scroll unless the user scrolled up
  const inFlightRef = useRef(false);

  /* reload history when switching between proposals */
  useEffect(() => {
    setMessages(loadHistory(proposalId));
  }, [proposalId]);

  /* persist on every settled change (pending state lives outside messages) */
  useEffect(() => {
    saveHistory(proposalId, messages);
  }, [proposalId, messages]);

  /* elapsed ticker while a request is in flight */
  useEffect(() => {
    if (!inFlight) return undefined;
    const started = Date.now();
    setElapsedMs(0);
    const t = window.setInterval(() => setElapsedMs(Date.now() - started), 250);
    return () => window.clearInterval(t);
  }, [inFlight]);

  /* stick to bottom on new content unless the user scrolled up */
  useLayoutEffect(() => {
    const el = listRef.current;
    if (el && stickRef.current) el.scrollTop = messages.length ? el.scrollHeight : 0;
  }, [messages, inFlight, open]);

  const onListScroll = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  }, []);

  /* focus the composer when the panel opens */
  useEffect(() => {
    if (open) {
      const id = window.setTimeout(() => taRef.current?.focus({ preventScroll: true }), 220);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [open]);

  const push = useCallback((m: ChatMessage) => {
    setMessages((xs) => [...xs, m].slice(-HISTORY_CAP));
  }, []);

  const growTa = useCallback(() => {
    const ta = taRef.current;
    if (ta) autoGrow(ta);
  }, []);
  useLayoutEffect(growTa, [input, growTa]);

  const runSend = useCallback(
    async (instruction: string, modelOverride?: string, modeOverride?: 'ask'|'edit') => {
      const text = instruction.trim();
      if (!text || inFlightRef.current || saving) return;
      const requestMode = modeOverride || (readOnly ? 'ask' : mode);
      if (requestMode === 'edit' && readOnly) return;
      if (requestMode === 'ask' && !onAsk) return;
      const useModel = modelOverride || model;
      stickRef.current = true;
      push({ id: nextId(), role: 'user', text });
      setInput('');
      inFlightRef.current = true;
      setInFlight(true);
      let savedNoted = false;
      let res: ChatEditResult;
      try {
        if (requestMode === 'ask') {
          const answer = await onAsk!(text, useModel);
          push({ id: nextId(), role: 'answer', text: answer.answer });
          return;
        }
      res = await onSend(text, useModel, {
        onSavedManual: () => {
          if (savedNoted) return;
          savedNoted = true;
          push({ id: nextId(), role: 'system', text: 'Saved your manual edits' });
        },
      });
      } catch (error) {
        res = { ok: false, error: (error as Error).message || 'The request could not complete. Try again.' };
      } finally {
      inFlightRef.current = false;
      setInFlight(false);
      }
      if (res.ok && res.after) {
        const chips = res.before ? computeDiff(res.before, res.after) : [];
        push({
          id: nextId(),
          role: 'assistant',
          status: 'Document updated — status back to review',
          chips,
          telemetry: res.telemetry ? telemetryText(res.telemetry) : null,
        });
      } else {
        push({
          id: nextId(),
          role: 'error',
          text: res.error || 'The edit could not be completed — try again',
          instruction: text,
          model: useModel,
          mode: requestMode,
        });
      }
    },
    [model, mode, readOnly, onAsk, onSend, push, saving],
  );

  const retry = useCallback(
    (instruction: string, m: string, retryMode?: 'ask'|'edit') => {
      setModel(m);
      void runSend(instruction, m, retryMode);
    },
    [runSend],
  );

  const clearHistory = useCallback(() => {
    if (inFlightRef.current) return;
    if (!messages.length) return;
    if (!window.confirm('Clear this chat history?')) return;
    setMessages([]);
  }, [messages.length]);

  const composerSubmit = useCallback(() => {
    void runSend(input);
  }, [runSend, input]);

  const busy = inFlight;
  const panelDrag = useLatoAiPanelDrag(open);
  const canSend = input.trim().length > 0 && !busy && !saving && (mode === 'ask' ? !!onAsk : !readOnly);

  return (
    <>
      {onOpen && <LatoAiLauncher open={open} busy={busy} disabled={!open && openDisabled} editOnly={!onAsk} onClick={open ? onClose : onOpen} />}
      <div
        className={'prop-chat-backdrop' + (open ? ' open' : '')}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        ref={panelDrag.panelRef}
        style={panelDrag.panelStyle}
        className={'prop-chat lato-ai-panel' + (open ? ' open' : '') + panelDrag.panelClass}
        inert={!open}
        aria-label="AI editor"
        aria-hidden={open ? undefined : true}
      >
        <div className="prop-chat-panel">
          <button
            type="button"
            className="prop-chat-drag"
            aria-label="Close AI editor"
            onClick={onClose}
          />
          <header className="prop-chat-head" {...panelDrag.handleProps} title="Drag to move Lato AI">
          <LatoOrb animated={open} busy={busy} />
            <div className="prop-chat-head-text">
              <div className="prop-chat-title">Lato AI</div>
              <div className="prop-chat-hint">Your document, thoughtfully refined.</div>
            </div>
            <button
              className="prop-chat-headbtn"
              title="Clear chat history"
              aria-label="Clear chat history"
              onClick={clearHistory}
              disabled={busy || !messages.length}
            >
              <Trash2 size={15} aria-hidden="true" />
            </button>
            <button
              className="prop-chat-headbtn"
              title="Close AI editor"
              aria-label="Close AI editor"
              onClick={onClose}
            >
              <X size={16} aria-hidden="true" />
            </button>
          </header>

          <div className="prop-chat-list" ref={listRef} onScroll={onListScroll}>
            {messages.length === 0 && !busy ? (
              <div className="prop-chat-empty">
                <div className="prop-chat-empty-mark"><LatoOrb /></div>
                <div className="prop-chat-empty-title">{mode === 'ask' ? 'Ask about this proposal' : 'What would you like to refine?'}</div>
                <div className="prop-chat-empty-chips">
                  {(mode === 'ask' ? ['What does this proposal include?', 'Explain the delivery timeline', 'Summarize the monthly fees'] : QUICK_PROMPTS).map((q) => (
                    <button
                      key={q}
                      type="button"
                      className="prop-chat-quick"
                      onClick={() => {
                        setInput(q);
                        taRef.current?.focus({ preventScroll: true });
                      }}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {messages.map((m) => {
              if (m.role === 'user') {
                return (
                  <div key={m.id} className="prop-chat-msg user">
                    <div className="prop-chat-bubble user">{m.text}</div>
                  </div>
                );
              }
              if (m.role === 'system') {
                return (
                  <div key={m.id} className="prop-chat-system">
                    {m.text}
                  </div>
                );
              }
              if (m.role === 'answer') return <div key={m.id} className="prop-chat-msg assistant"><div className="prop-chat-bubble assistant">{m.text}</div></div>;
              if (m.role === 'error') {
                return (
                  <div key={m.id} className="prop-chat-msg assistant">
                    <div className="prop-chat-bubble error">
                      <div className="prop-chat-err-text">{m.text}</div>
                      <button
                        type="button"
                        className="prop-chat-retry"
                        onClick={() => retry(m.instruction, m.model, m.mode)}
                        disabled={busy || saving}
                      >
                        ↻ Retry
                      </button>
                    </div>
                  </div>
                );
              }
              // assistant
              return (
                <div key={m.id} className="prop-chat-msg assistant">
                  <div className="prop-chat-bubble assistant">
                    <div className="prop-chat-status">{m.status}</div>
                    {m.chips.length ? (
                      <div className="prop-chat-chips">
                        {m.chips.map((c) => (
                          <span key={c.id} className={'prop-chat-chip ' + c.kind}>
                            {c.label}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="prop-chat-chips-none">No structural changes detected.</div>
                    )}
                    {m.telemetry ? <div className="prop-chat-tele">{m.telemetry}</div> : null}
                  </div>
                </div>
              );
            })}

            {busy ? (
              <div className="prop-chat-msg assistant">
                <div className="prop-chat-bubble assistant pending">
                  <span className="prop-chat-dots" aria-hidden="true">
                    <i />
                    <i />
                    <i />
                  </span>
                  <span className="prop-chat-pending-text">
                    {mode === 'ask' ? 'Reading the proposal…' : 'Editing the document…'} {fmtElapsedLocal(elapsedMs)}
                  </span>
                </div>
              </div>
            ) : null}
          </div>

          <form
            className="prop-chat-composer"
            onSubmit={(e) => {
              e.preventDefault();
              composerSubmit();
            }}
          >
            <div className="arch-chat-modes" role="group" aria-label="Assistant mode">
              <button type="button" className="arch-chat-mode" disabled={busy || !onAsk} aria-pressed={mode === 'ask'} onClick={() => setMode('ask')}><MessageCircle size={14} /> Ask</button>
              <button type="button" className="arch-chat-mode" disabled={busy || readOnly} aria-pressed={mode === 'edit'} onClick={() => setMode('edit')}><Pencil size={14} /> Edit</button>
            </div>
            <textarea
              ref={taRef}
              className="prop-chat-input"
              rows={1}
              value={input}
              placeholder={mode === 'ask' ? 'Ask about scope, timeline or fees…' : 'Describe the change…'}
              disabled={busy}
              aria-label="Message the AI editor"
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  e.preventDefault();
                  composerSubmit();
                }
              }}
            />
            <div className="prop-chat-composer-row">
              <button
                type="submit"
                className="arch-btn arch-btn-primary arch-btn-sm prop-chat-send"
                disabled={!canSend}
              >
                <ArrowUp size={19} aria-hidden="true" /><span className="lato-ai-sr">Send</span>
              </button>
            </div>
            {busy ? (
              <div className="prop-chat-pending-inline" aria-hidden="true">
                <span className="prop-chat-dots">
                  <i />
                  <i />
                  <i />
                </span>
                Working… {fmtElapsedLocal(elapsedMs)}
              </div>
            ) : null}
          </form>
        </div>
      </aside>
    </>
  );
}

/** M:SS elapsed (kept local to avoid coupling to utils' formatter set). */
function fmtElapsedLocal(ms: number): string {
  const s = Math.floor(ms / 1000);
  return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');
}
